import './Table.css';
import { useRef, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUp, faArrowDown, faArrowLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons';

import PhotoUpload from './PhotoUpload';
import './Photo.css';
const TOGGLE_MODES  = ["single", "set"];
const TOGGLE_LABELS = ["Single", "Carton"];
const TOGGLE_COLORS = ["#419fc4", "#1f4e61"];
function suggestScanStrategy(required, setQty) {
  if (!setQty) return null;
  if (setQty <= 1) {
    // carton size not meaningful, just show all singles
    return { sets: 0, singles: required, setQty };
  }
  const sets = Math.floor(required / setQty);
  const singles = required % setQty;
  return { sets, singles, setQty };
}
function ToggleMode({ currentMode = "single", setQuantity = 1, subSetQuantity = 1, onToggle, setLabel = 'Carton' }) {
  const modeIndex = TOGGLE_MODES.indexOf(currentMode);
  const quantities = { single: 1, subset: subSetQuantity, set: setQuantity };

  return (
    <div className="toggle-mode-container">
<div
  className="slider"
  style={{
    transform: modeIndex === 0 ? 'translateX(0)' : 'translateX(calc(100% + 2px))',
    backgroundColor: TOGGLE_COLORS[modeIndex],
  }}
/>
{TOGGLE_MODES.map((mode, index) => (
  <span
    key={mode}
    className={index === modeIndex ? "active" : ""}
    onClick={() => onToggle(mode)}
  >
    {mode === "set" ? `${setLabel.split(' ')[0]} (${setQuantity})` : TOGGLE_LABELS[index]}
  </span>
))}
    </div>
  );
}

function TableFormat({
  data,
  onManualVerify,
  onManualIncrement,
  onManualQuantityInput,
  onPhotoChange,
  itemPhotos,
  toggleModes,
  onToggleModeChange,
  onEditCartonSize,
  setLabel = 'Carton',  // 👈 add this
  maxPhotosPerItem = 4,
  showWarehouseLocation = true,
}) {
  const tableWrapperRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
const adjustInputRefs = useRef({});

  // Soft flash on a row when its scanned count changes: green going up (scan, arrow, qty box, verify), red going down.
  // a/b alternate so scanning the same row twice in a row restarts the animation.
  const prevScanned = useRef(null);
  const flashTimer = useRef(null);
  const [flash, setFlash] = useState({ key: null, n: 0, down: false });
  useEffect(() => {
    if (!Array.isArray(data)) return;
    const current = {};
    data.forEach(d => { current[d.rowKey] = d.scannedCount; });
    const prev = prevScanned.current;
    prevScanned.current = current;
    if (!prev) return; // first load or a restored draft: nothing just happened
    const changed = data.find(d => prev[d.rowKey] !== undefined && current[d.rowKey] !== prev[d.rowKey]);
    if (!changed) return;
    setFlash(f => ({ key: changed.rowKey, n: f.n + 1, down: current[changed.rowKey] < prev[changed.rowKey] }));
    clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlash(f => ({ ...f, key: null })), 900);
  }, [data]);
  useEffect(() => () => clearTimeout(flashTimer.current), []);

  useEffect(() => {
    function checkOverflow() {
      const el = tableWrapperRef.current;
      if (el) setIsOverflowing(el.scrollWidth > el.clientWidth);
    }
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, []);

  if (!data || Object.keys(data).length === 0) {
    return (
      <div style={{
        padding: '2.5rem',
        textAlign: 'center',
        color: 'var(--text-muted)',
        fontFamily: 'var(--font-ui)',
        fontSize: '0.9rem',
      }}>
        No data to display.
      </div>
    );
  }

  return (
    <div className="table-wrapper">
      {isOverflowing && (
        <div className="scroll-pill">
          <FontAwesomeIcon icon={faArrowLeft} style={{ marginRight: 6 }} />
          Scroll to see all columns
          <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: 6 }} />
        </div>
      )}

      <div className="table-scroll" ref={tableWrapperRef}>
        <table className="barcode-table">
          <thead>
            <tr>
              <th>Scan Status</th>
<th>Item</th>
              <th>Required</th>
              <th>Scanned</th>
              <th>
                Mode
<div style={{ fontSize: '0.6rem', fontWeight: 400, opacity: 0.75, marginTop: 2 }}>
  Single / {setLabel.split(' ')[0]}
</div>
              </th>
              <th>Photos</th>
              <th>Adjust</th>
            </tr>
          </thead>

          <tbody>
            {data.map(({
              barcode,
              rowKey,
              quantity,
              scannedCount,
              individualScanned = 0,
              subSetScanned = 0,
              setScanned = 0,
              status,
              isManual,
              subSetQuantity,
              setQuantity,
              location,
            }) => (
              <tr key={rowKey} className={`row-${status}${flash.key === rowKey ? (flash.down ? (flash.n % 2 ? ' row-flash-down-a' : ' row-flash-down-b') : (flash.n % 2 ? ' row-flash-a' : ' row-flash-b')) : ''}`}>

<td data-label="Scan Status">
  <div className={`scan-status ${status}`}>
    <span className={`badge ${status}`}>
      {status === 'scanned' ? 'Complete' 
        : status === 'incomplete' ? 'Incomplete' 
        : 'Start Scanning'}
    </span>
    {status !== 'scanned' && isManual && (
      <button
        onClick={() => onManualVerify(rowKey)}
        className="photo-upload-label"
        style={{ marginTop: '4px' }}
      >
        Verify Quantity
      </button>
    )}
  </div>
</td>

<td data-label="Item">
  <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
    <span style={{
      fontSize: 17,
      fontWeight: 600,
      color: 'var(--text-primary)',
      letterSpacing: '0.02em',
      textAlign: 'left'
    }}>
      {barcode}
    </span>
    {showWarehouseLocation && (
      <span style={{
        fontSize: '0.85rem',
        color: 'gray',
        display: 'flex',
        gap: '4px',
      }}>
        Location: {location}
      </span>
    )}
  </div>
</td>

<td data-label="Required">
  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
    <span style={{ fontWeight: 700, fontSize: 20 }}>{quantity}</span>

    {(() => {
      const suggestion = suggestScanStrategy(quantity, setQuantity);
      if (!suggestion) return null;
      return (
<div className="scan-suggestion">
  <span className="suggestion-label">suggestion:</span>
  <div className="suggestion-chips">
    {suggestion.sets > 0 && (
<span className="suggestion-chip carton">{suggestion.sets} {setLabel.split(' ')[0].toLowerCase()}s</span>

    )}
{suggestion.singles > 0 && (
  <span className="suggestion-chip single">
    {suggestion.sets > 0 ? '+' : ''}{suggestion.singles} singles
  </span>
)}
    {suggestion.sets === 0 && suggestion.singles === 0 && (
      <span className="suggestion-chip single">0 singles</span>
    )}
  </div>
</div>
      );
    })()}
  </div>
</td>
<td data-label="Scanned">
  <div className="scanned-breakdown">
    <span style={{fontSize: 17}}><strong>Single:</strong> {individualScanned}</span>
{setScanned > 0 && (
  <span style={{fontSize:17}}><strong> {setLabel.split(' ')[0]} ({setQuantity}):</strong> {setScanned}</span>
)}
    <span className="scan-total" style={{ fontSize: 17}}><strong>Total:</strong> {scannedCount}</span>
  </div>
</td>

                {/* Toggle */}
<td data-label="Mode">
<ToggleMode
  currentMode={toggleModes?.[rowKey] || "single"}
  setQuantity={setQuantity}
  subSetQuantity={subSetQuantity}
  onToggle={(mode) => onToggleModeChange(rowKey, mode)}
  setLabel={setLabel}  // 👈 add this
/>
{onEditCartonSize && (
  <button
    type="button"
    onClick={() => onEditCartonSize(rowKey, barcode, setQuantity)}
    style={{
      display: 'block', marginTop: 4, background: 'none', border: 'none',
      padding: 0, fontSize: '0.75rem', color: '#419fc4', cursor: 'pointer',
      textDecoration: 'underline',
    }}
  >
    Edit {setLabel.split(' ')[0].toLowerCase()} size
  </button>
)}
</td>

                {/* Photos */}
                <td data-label="Photos">
<PhotoUpload
  itemKey={rowKey}
  photos={itemPhotos[rowKey] || []}
  onPhotoChange={onPhotoChange}
  maxPhotos={maxPhotosPerItem}  // 👈 add this
/>
                </td>

<td data-label="Adjust">
  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
    <button
      className="adjust-button up"
      onClick={() => onManualIncrement(rowKey, 1)}
      title="Increase"
    >
      <FontAwesomeIcon icon={faArrowUp} />
    </button>
    <button
      className="adjust-button down"
      onClick={() => onManualIncrement(rowKey, -1)}
      title="Decrease"
    >
      <FontAwesomeIcon icon={faArrowDown} />
    </button>
    <input
      type="number"
      placeholder="Qty"
      min="1"
      className="adjust-input"
      ref={el => adjustInputRefs.current[rowKey] = el}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          const qty = parseInt(e.target.value, 10);
          if (!isNaN(qty) && qty > 0) {
            onManualQuantityInput(rowKey, qty);
            e.target.value = '';
          }
        }
      }}
    />
    <button
      title="Confirm quantity"
      onClick={() => {
        const input = adjustInputRefs.current[rowKey];
        const qty = parseInt(input?.value, 10);
        if (!isNaN(qty) && qty > 0) {
          onManualQuantityInput(rowKey, qty);
          if (input) input.value = '';
        }
      }}
      style={{
        height: 32,
        padding: '0 10px',
        background: '#419fc4',
        color: '#fff',
        border: 'none',
        borderRadius: 6,
        fontSize: 16,
        fontWeight: 700,
        cursor: 'pointer',
        flexShrink: 0,
      }}
    >
      +
    </button>
  </div>
</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default TableFormat;